import { Teacher, ClassTimeslot, Subject, Room, YardDutySlot, YardDutyLocation, Student } from './classes.js';
import globals from '@/globals.js';
import timetable from './timetable.js';
import router from './router/index.js';
import { hhmmToMinutes, shortNameForPeriod } from './timeUtil.js';

function rowKeyFor(start, end, name) {
    return `${start}|${end}|${name}`;
}

function pushInto(map, key, value) {
    if (key === undefined || key === null || key === '') return;
    if (!map[key]) map[key] = [];
    map[key].push(value);
}

// The v10 contract is: every lesson resolves to a definition with a valid time.
// When it doesn't we skip the entry (never mis-place it) — but say so loudly, in
// the console for whoever monitors the feed and in the footer for everyone else,
// so a bad upstream export is visible instead of silently thinning timetables.
function reportDataIssues(missingDefinitions, invalidDefinitions) {
    if (!missingDefinitions.length && !invalidDefinitions.length) {
        globals.dataWarning = null; // clear any stale warning from a previous load
        return;
    }
    var parts = [];
    if (missingDefinitions.length) {
        parts.push(missingDefinitions.length + ' lesson' + (missingDefinitions.length === 1 ? '' : 's') +
            ' referenced a missing period definition');
    }
    if (invalidDefinitions.length) {
        parts.push(invalidDefinitions.length + ' period definition' + (invalidDefinitions.length === 1 ? '' : 's') +
            ' had invalid times');
    }
    var summary = 'Timetable data issue: ' + parts.join('; ') +
        ' — affected entries were skipped, not mis-placed. Check the source export.';
    console.error(summary, { missingDefinitions: missingDefinitions, invalidDefinitions: invalidDefinitions });
    globals.dataWarning = summary;
}

export default function loadEverything(timetable_data) {
    console.log('loadEverything (v' + (timetable_data.version || '?') + ')');
    timetable.empty();

    timetable.coordinate = timetable_data.coordinate || null;
    if (timetable_data.heads_of_house) timetable.heads_of_house = timetable_data.heads_of_house;
    if (timetable_data.canvas_exceptions) timetable.canvas_exceptions = timetable_data.canvas_exceptions;

    // Data-contract issues collected during load and reported loudly at the end,
    // so upstream drift can't silently mis-place (or drop) a lesson unnoticed.
    var invalidDefinitions = [];
    var missingDefinitions = [];

    // --- Definitions (the bell schedules) ---
    (timetable_data.timetable_definitions || []).forEach(def => {
        var startMin = hhmmToMinutes(def.start);
        var endMin = hhmmToMinutes(def.end);
        // A period we can't place in time is worse than useless — indexing it would
        // put every lesson on it at the top of the day. Skip it; lessons that
        // reference it then surface below as "missing definition".
        if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) {
            invalidDefinitions.push(def);
            return;
        }
        var d = {
            seq: def.seq,
            cycle_day_index: def.day,
            day: def.day,
            group: def.group,
            period: def.period,
            periodSeq: def.periodSeq,
            name: def.name,
            short_name: shortNameForPeriod(def.name),
            start: def.start,
            end: def.end,
            startMin: startMin,
            endMin: endMin,
            rowKey: rowKeyFor(def.start, def.end, def.name)
        };
        timetable.definitions.push(d);
        timetable.definitionsBySeqDay[`${d.seq}|${d.day}`] = d;
        var gk = `${d.group}|${d.day}`;
        if (!timetable.definitionsByGroupDay[gk]) timetable.definitionsByGroupDay[gk] = [];
        timetable.definitionsByGroupDay[gk].push(d);
    });

    // --- Teachers ---
    Object.values(timetable_data.teachers || {}).forEach(t => {
        var teacher = new Teacher(t);
        timetable.teachers[teacher.SchoolStaffCode] = teacher;
    });

    // --- Subjects (the class catalogue) ---
    Object.entries(timetable_data.classes || {}).forEach(([code, info]) => {
        timetable.subjects[code] = new Subject({
            code: code,
            name: info.description,
            campus: info.campus,
            timeslots: [],
            teachers: []
        });
    });

    // --- Lessons (each references a definition) ---
    (timetable_data.lessons || []).forEach(lesson => {
        var def = timetable.definitionFor(lesson.seq, lesson.day);
        if (!def) {
            missingDefinitions.push(lesson);
            return;
        }
        var subject = timetable.subjects[lesson.class_code];
        if (!subject) {
            // A lesson for a class not in the catalogue — create a bare subject.
            subject = new Subject({ code: lesson.class_code, name: lesson.class_code, timeslots: [], teachers: [] });
            timetable.subjects[lesson.class_code] = subject;
        }

        var slot = new ClassTimeslot({
            class_code: lesson.class_code,
            room_code: lesson.room,
            teacher_short_name: lesson.teacher,
            cycle_day_index: lesson.day,
            seq: lesson.seq,
            group: def.group,
            period: def.period,
            name: def.name,
            short_name: def.short_name,
            start: def.start,
            end: def.end,
            startMin: def.startMin,
            endMin: def.endMin,
            rowKey: def.rowKey,
            ClassDescription: subject.getName()
        });

        subject.timeslots.push(slot);
        timetable.class_timeslots.push(slot);
        pushInto(timetable.timeslotsByDayRow, `${lesson.day}|${def.rowKey}`, slot);
        pushInto(timetable.timeslotsByTeacher, lesson.teacher, slot);
        pushInto(timetable.timeslotsByRoom, lesson.room, slot);

        if (lesson.room && !timetable.rooms[lesson.room]) {
            timetable.rooms[lesson.room] = new Room({ ROOM: lesson.room, CODE: lesson.room });
        }

        var teacher = timetable.teachers[lesson.teacher];
        if (teacher && teacher.subjects.indexOf(lesson.class_code) === -1) {
            teacher.subjects.push(lesson.class_code);
        }
    });

    // --- Teacher <-> class links (not all imply a scheduled lesson) ---
    (timetable_data.teacher_classes || []).forEach(tc => {
        var subject = timetable.subjects[tc.class_code];
        if (!subject || !tc.teacher) return;
        if (subject.teachers.indexOf(tc.teacher) === -1) subject.teachers.push(tc.teacher);
        var teacher = timetable.teachers[tc.teacher];
        if (teacher && teacher.subjects.indexOf(tc.class_code) === -1) teacher.subjects.push(tc.class_code);
    });

    // Reverse index: teacher -> subjects they're listed on (for Teacher.getClasses).
    Object.values(timetable.subjects).forEach(subject => {
        subject.teachers.forEach(code => pushInto(timetable.subjectsByTeacher, code, subject));
    });

    // --- Students ---
    Object.values(timetable_data.students || {}).forEach(s => {
        var student = new Student(s);
        timetable.students[student.StudentID] = student;
    });

    // --- Yard duties ---
    (timetable_data.yard_duties || []).forEach(yd => {
        var ydStartMin = hhmmToMinutes(yd.start);
        var ydEndMin = hhmmToMinutes(yd.end);
        // Same defence as period definitions: a duty we can't place in time would
        // poison the startMin sort in periodRows, so skip and report it rather than
        // let a NaN drift a whole day's layout.
        if (!Number.isFinite(ydStartMin) || !Number.isFinite(ydEndMin) || ydEndMin <= ydStartMin) {
            invalidDefinitions.push(yd);
            return;
        }
        var slot = new YardDutySlot({
            SchoolStaffCode: yd.staff,
            ClassDescription: yd.location,
            DefinitionDescription: yd.name,
            DefinitionTimeFrom: yd.start,
            DefinitionTimeTo: yd.end,
            DayNumber: yd.day,
            cycle_day_index: yd.day,
            name: yd.name,
            short_name: shortNameForPeriod(yd.name),
            start: yd.start,
            end: yd.end,
            startMin: ydStartMin,
            endMin: ydEndMin,
            rowKey: rowKeyFor(yd.start, yd.end, yd.name)
        });
        var teacher = timetable.teachers[yd.staff];
        if (teacher) teacher.yardDuties.push(slot);
        timetable.yard_duties.push(slot);

        if (!timetable.yard_duty_locations[yd.location]) {
            timetable.yard_duty_locations[yd.location] = new YardDutyLocation({ ClassDescription: yd.location });
        }
        timetable.yard_duty_locations[yd.location].yard_duties.push(slot);
    });

    reportDataIssues(missingDefinitions, invalidDefinitions);

    globals.loaded = true;
    console.log('Finished loading timetable data',
        Object.keys(timetable.students).length, 'students,',
        Object.keys(timetable.teachers).length, 'teachers,',
        timetable.class_timeslots.length, 'lessons');

    if (globals.LOGIN_REDIRECT) {
        router.push(globals.LOGIN_REDIRECT);
        globals.LOGIN_REDIRECT = null;
    } else if (router.currentRoute.value.name === 'login') {
        router.push('/');
    }
}
